<template>
  <div class="faq-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>{{ t('faq.pageTitle') }}</h1>
        <p class="page-subtitle">{{ t('faq.pageSubtitle') }}</p>
      </div>
    </section>

    <!-- FAQ Content -->
    <section class="faq-content">
      <div class="container">
        <!-- Game Selection -->
        <div class="game-filter">
          <button
            v-for="game in gameOptions"
            :key="game.code"
            :class="{ active: selectedGame === game.code }"
            @click="selectedGame = game.code"
            class="filter-btn"
          >
            {{ game.name }}
          </button>
        </div>

        <!-- FAQ Items -->
        <div class="faq-container">
          <div
            v-for="(item, index) in getFAQItems()"
            :key="index"
            class="faq-item"
            :class="{ active: activeFAQ === index }"
          >
            <button class="faq-question" @click="toggleFAQ(index)">
              <span>{{ item.q }}</span>
              <svg class="faq-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div v-if="activeFAQ === index" class="faq-answer animate-fade-in">
              {{ item.a }}
            </div>
          </div>
        </div>

        <!-- Still Need Help -->
        <div class="help-section">
          <h2>{{ t('faq.stillNeedHelp') }}</h2>
          <p>{{ t('faq.contactSupport') }}</p>
          <div class="help-buttons">
            <a href="#/support" class="help-btn">{{ t('nav.support') }}</a>
            <a href="#/contact" class="help-btn secondary">{{ t('nav.contact') }}</a>
          </div>
        </div>

        <!-- Back Button -->
        <button class="btn-back" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()
const selectedGame = ref('platform')
const activeFAQ = ref(null)

const gameOptions = computed(() => [
  { code: 'platform', name: t('faq.tabs.platform') || 'Platform' },
  { code: 'poker', name: t('games.poker.title') || 'Word Poker' },
  { code: 'sudoku', name: t('games.sudoku.title') || 'Sudoku' },
  { code: 'riddles', name: t('games.riddles.title') || 'Brain Teasers' }
])

const faqData = computed(() => ({
  platform: [
    { q: t('faq.platform.q1'), a: t('faq.platform.a1') },
    { q: t('faq.platform.q2'), a: t('faq.platform.a2') },
    { q: t('faq.platform.q3'), a: t('faq.platform.a3') },
    { q: t('faq.platform.q4'), a: t('faq.platform.a4') }
  ],
  poker: [
    { q: t('faq.poker.q1'), a: t('faq.poker.a1') },
    { q: t('faq.poker.q2'), a: t('faq.poker.a2') },
    { q: t('faq.poker.q3'), a: t('faq.poker.a3') }
  ],
  sudoku: [
    { q: t('faq.sudoku.q1'), a: t('faq.sudoku.a1') },
    { q: t('faq.sudoku.q2'), a: t('faq.sudoku.a2') },
    { q: t('faq.sudoku.q3'), a: t('faq.sudoku.a3') }
  ],
  riddles: [
    { q: t('faq.riddles.q1'), a: t('faq.riddles.a1') },
    { q: t('faq.riddles.q2'), a: t('faq.riddles.a2') },
    { q: t('faq.riddles.q3'), a: t('faq.riddles.a3') }
  ]
}))

const getFAQItems = () => {
  return faqData.value[selectedGame.value] || []
}

const toggleFAQ = (index) => {
  activeFAQ.value = activeFAQ.value === index ? null : index
}

const goHome = () => {
  window.location.hash = '#/'
}
</script>

<style scoped>
.faq-page {
  min-height: 100vh;
  background: var(--color-bg-light);
}

/* Header */
.page-header {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-5xl) var(--space-base);
  text-align: center;
}

.page-header h1 {
  font-size: clamp(28px, 6vw, 48px);
  margin: 0 0 var(--space-base) 0;
  font-weight: var(--font-weight-bold);
}

.page-subtitle {
  font-size: var(--font-size-body);
  margin: 0;
  opacity: 0.95;
}

/* Content */
.container {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--space-4xl) var(--space-base);
}

.faq-content {
  background: var(--color-bg-light);
}

/* Game Filter */
.game-filter {
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-bottom: var(--space-3xl);
  flex-wrap: wrap;
}

.filter-btn {
  padding: var(--space-sm) var(--space-lg);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.filter-btn:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
}

.filter-btn.active {
  background: var(--gradient-primary);
  color: white;
  border-color: var(--color-primary-orange);
}

/* FAQ Container */
.faq-container {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-4xl);
}

.faq-item {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: all var(--transition-base) var(--ease-in-out));
}

.faq-item:hover {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-md);
}

.faq-item.active {
  border-color: var(--color-primary-orange);
  box-shadow: var(--shadow-md);
}

.faq-question {
  width: 100%;
  background: var(--color-bg-card);
  border: none;
  padding: var(--space-lg);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-base);
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
}

.faq-question:hover {
  background: rgba(255, 107, 53, 0.05);
  color: var(--color-primary-orange);
}

.faq-item.active .faq-question {
  background: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
}

.faq-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform var(--transition-base) var(--ease-in-out));
}

.faq-item.active .faq-icon {
  transform: rotate(180deg);
}

.faq-answer {
  padding: var(--space-lg);
  background: var(--color-bg-light);
  border-top: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-size: var(--font-size-small);
}

/* Help Section */
.help-section {
  background: var(--gradient-primary);
  color: white;
  padding: var(--space-3xl);
  border-radius: var(--radius-lg);
  text-align: center;
  margin-bottom: var(--space-3xl);
}

.help-section h2 {
  font-size: var(--font-size-h4);
  margin: 0 0 var(--space-md) 0;
  font-weight: var(--font-weight-bold);
}

.help-section p {
  font-size: var(--font-size-body);
  margin: 0 0 var(--space-2xl) 0;
  opacity: 0.95;
}

.help-buttons {
  display: flex;
  gap: var(--space-lg);
  justify-content: center;
  flex-wrap: wrap;
}

.help-btn {
  display: inline-block;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-primary-orange);
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
  border: 2px solid white;
}

.help-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.help-btn.secondary {
  background: transparent;
  border-color: white;
}

.help-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Back Button */
.btn-back {
  display: block;
  margin: 0 auto;
  padding: var(--space-md) var(--space-2xl);
  background: var(--color-bg-light);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
  font-size: var(--font-size-body);
}

.btn-back:hover {
  border-color: var(--color-primary-orange);
  color: var(--color-primary-orange);
  background: rgba(255, 107, 53, 0.05);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

/* Responsive */
@media (max-width: 640px) {
  .page-header {
    padding: var(--space-3xl) var(--space-base);
  }

  .container {
    padding: var(--space-2xl) var(--space-base);
  }

  .game-filter {
    gap: var(--space-sm);
  }

  .filter-btn {
    padding: var(--space-sm) var(--space-md);
    font-size: var(--font-size-small);
  }

  .faq-question {
    padding: var(--space-base);
    font-size: var(--font-size-small);
  }

  .faq-answer {
    padding: var(--space-base);
  }

  .help-section {
    padding: var(--space-2xl);
    margin-bottom: var(--space-2xl);
  }

  .help-buttons {
    flex-direction: column;
    gap: var(--space-md);
  }

  .help-btn {
    width: 100%;
  }

  .btn-back {
    width: 100%;
  }
}
</style>
